import { getGameRuntimeConfig } from "./config";
import { CLIENT_BUSINESS_CODE } from "./business-codes";
import { ApiError, apiPost, apiPostStream } from "./http";
import { showLlmErrorNotice } from "./llm-error-notice";
import { ModelClient, readPreferredModelId } from "./models";
import { showConfiguredEntitlementDialogForAccessError } from "../entitlement";
import { acquireAiInteractionLock, AiInteractionBusyError, isAiInteractionLocked } from "../ai-interaction-lock";
import { createId } from "../utils/crypto";
import { normalizeModelResponseText } from "./model-json";
import { normalizeNarrativeResponse, extractNarrativeText } from "./narrative-response";
export { extractNarrativeText, readNarrativeRecord, parseModelJson, cleanNarrativeText } from "./narrative-response";
export { repairModelJsonText, normalizeModelResponseText, ModelResponseFormatError } from "./model-json";

export interface LlmMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface LlmChatInput {
  token: string;
  conversationId: string;
  idempotencyKey: string;
  messages: LlmMessage[];
  packageId?: number;
  modelId?: number;
  timeoutMs?: number;
  responseMode?: "structured" | "narrative" | "state-markup";
}

export type LlmStreamHandler = (delta: string, accumulated: string) => void;

type UnknownRecord = Record<string, unknown>;

type ResolvedModelCacheEntry = {
  preferredModelId?: number;
  fallbackModelId?: number;
  savedModelId?: number;
  resolvedModelId: number;
};

const resolvedModelCache = new Map<string, ResolvedModelCacheEntry>();
const legacyRequestIds = new Map<string, string>();
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const DEFAULT_LLM_TIMEOUT_MS = 300_000;

export function resolveLlmTimeoutMs(timeoutMs?: number) {
  return Math.max(DEFAULT_LLM_TIMEOUT_MS, timeoutMs ?? DEFAULT_LLM_TIMEOUT_MS);
}

export function normalizeIdempotencyKey(value: string) {
  const candidate = value.trim();
  if (UUID_PATTERN.test(candidate)) return candidate;
  if (!candidate) return createId();
  const existing = legacyRequestIds.get(candidate);
  if (existing) return existing;
  const id = createId();
  if (legacyRequestIds.size >= 512) legacyRequestIds.delete(legacyRequestIds.keys().next().value!);
  legacyRequestIds.set(candidate, id);
  return id;
}

function normalizeCompletion(raw: string, mode: LlmChatInput["responseMode"]) {
  if (mode === "narrative" || mode === "state-markup" && /^\s*(?:\{|```json)/i.test(raw)) return normalizeNarrativeResponse(raw);
  return normalizeModelResponseText(raw);
}

function normalizeMessages(messages: LlmMessage[]) {
  if (!messages.length) {
    throw new Error("LLM 请求至少需要一条消息");
  }
  return messages.map((message) => {
    const content = message.content.trim();
    if (!content) {
      throw new Error("LLM 消息内容不能为空");
    }
    // 保留游戏构造的角色和数组顺序，后台会在整个列表之前统一插入运营模板。
    return { role: message.role, content };
  });
}

function modelCacheKey(apiBaseUrl: string, packageId: number, token: string) {
  return `${apiBaseUrl}|${packageId}|${token}`;
}

async function resolveChatModel(token: string, packageId: number, preferredModelId: number | undefined, fallbackModelId: number | undefined, forceRefresh = false) {
  const config = getGameRuntimeConfig();
  const key = modelCacheKey(config.apiBaseUrl, packageId, token);
  const cached = resolvedModelCache.get(key);
  const savedModelId = readPreferredModelId(packageId);
  if (!forceRefresh && cached && cached.preferredModelId === preferredModelId && cached.fallbackModelId === fallbackModelId && cached.savedModelId === savedModelId) return cached.resolvedModelId;
  const { model } = await ModelClient.resolveAvailable({ token, packageId, preferredModelId, fallbackModelId });
  resolvedModelCache.set(key, { preferredModelId, fallbackModelId, savedModelId: readPreferredModelId(packageId), resolvedModelId: model.id });
  return model.id;
}

function isModelResolutionError(error: unknown) {
  return error instanceof ApiError && (
    error.code === CLIENT_BUSINESS_CODE.MODEL_ACCESS_DENIED
    || error.code === CLIENT_BUSINESS_CODE.RESOURCE_NOT_FOUND
  );
}

function textFromContent(content: unknown): string {
  if (typeof content === "string") return content.trim();

  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && item) {
          const record = item as UnknownRecord;
          return textFromContent(record.text ?? record.content);
        }
        return "";
      })
      .join("")
      .trim();
  }

  if (typeof content === "object" && content) {
    const record = content as UnknownRecord;
    return textFromContent(record.text ?? record.content ?? record.value);
  }

  return "";
}

function readChoiceContent(record: UnknownRecord): string {
  const choices = record.choices as unknown[] | undefined;
  const first = choices?.[0] as UnknownRecord | undefined;
  if (!first) return "";

  const message = first.message as UnknownRecord | undefined;
  return textFromContent(message?.content ?? first.delta ?? first.text ?? first.content);
}

function readContentFields(record: UnknownRecord): string {
  const content = textFromContent(record.content ?? record.text ?? record.output_text ?? record.answer ?? record.reply ?? record.response);
  if (content) return content;

  const result = record.result;
  if (typeof result === "string") return result.trim();
  if (typeof result === "object" && result) return readKnownContent(result);

  const message = record.message;
  if (typeof message === "object" && message) return readKnownContent(message);
  return "";
}

function readKnownContent(payload: unknown): string {
  if (typeof payload === "string") return payload.trim();
  if (!payload || typeof payload !== "object") return "";

  const record = payload as UnknownRecord;
  return readChoiceContent(record) || readContentFields(record);
}

function readContent(payload: unknown): string {
  const direct = readKnownContent(payload);
  if (direct) return direct;

  if (payload && typeof payload === "object") {
    const record = payload as UnknownRecord;
    const data = record.data;
    const dataText = readKnownContent(data);
    if (dataText) return dataText;
  }

  return "";
}

function readDelta(payload: unknown) {
  if (!payload || typeof payload !== "object") return "";
  const record = payload as UnknownRecord;
  const choices = record.choices as unknown[] | undefined;
  const first = choices?.[0] as UnknownRecord | undefined;
  const delta = first?.delta as UnknownRecord | undefined;
  return textFromContent(delta?.content ?? delta?.text);
}

function throwForLlmBusinessPayload(payload: unknown) {
  if (!payload || typeof payload !== "object") return;
  const record = payload as UnknownRecord;
  const code = record.code;
  if (typeof code !== "number" || code === CLIENT_BUSINESS_CODE.SUCCESS) return;
  const nested = record.data && typeof record.data === "object" ? record.data as UnknownRecord : undefined;
  const message = typeof record.message === "string" ? record.message : typeof nested?.message === "string" ? nested.message : `业务处理失败（${code}）`;
  throw new ApiError(message, 200, payload, code);
}

async function consumeStream(response: Response, onDelta?: LlmStreamHandler) {
  const contentType = response.headers.get("content-type") ?? "";
  if (!response.body || contentType.includes("application/json")) {
    const payload = JSON.parse(await response.text()) as unknown;
    throwForLlmBusinessPayload(payload);
    const content = readContent(payload);
    if (content) onDelta?.(content, content);
    return content;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulated = "";
  const processEvent = (event: string) => {
    const data = event.split(/\r?\n/).filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trimStart()).join("\n");
    if (!data || data === "[DONE]") return;
    const payload = JSON.parse(data) as unknown;
    throwForLlmBusinessPayload(payload);
    const delta = readDelta(payload);
    const full = delta || readContent(payload);
    if (!full) return;
    if (delta) accumulated += delta;
    else if (!accumulated || !full.startsWith(accumulated)) accumulated += full;
    else accumulated = full;
    onDelta?.(delta || full, accumulated);
  };
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const events = buffer.split(/\r?\n\r?\n/);
    buffer = events.pop() ?? "";
    for (const event of events) processEvent(event);
    if (done) break;
  }
  if (buffer.trim()) processEvent(buffer);
  return accumulated.trim();
}

export class LlmClient {
  /** Presentation requests may recover prose; authoritative schemas continue to use chat(). */
  static narrative(input: LlmChatInput): Promise<string> {
    return LlmClient.chat({ ...input, responseMode: "narrative" });
  }

  static narrativeStream(input: LlmChatInput, onDelta?: LlmStreamHandler): Promise<string> {
    return LlmClient.chatStream({ ...input, responseMode: "narrative" }, onDelta ? (_delta, accumulated) => {
      const text = extractNarrativeText(accumulated);
      if (text) onDelta(text, text);
    } : undefined);
  }

  static async text(input: LlmChatInput): Promise<string> {
    const text = extractNarrativeText(await LlmClient.narrative(input));
    if (!text) throw new Error("这次没有收到可用正文，请重试。");
    return text;
  }

  static async chat(input: LlmChatInput): Promise<string> {
    // React busy 状态可能尚未完成下一次渲染；共享出口同步拒绝第二个并发请求，避免重复计费。
    if (isAiInteractionLocked()) throw new AiInteractionBusyError();
    const releaseInteractionLock = acquireAiInteractionLock();
    try {
      const config = getGameRuntimeConfig();
      const packageId = input.packageId ?? config.packageId;
      const preferredModelId = input.modelId;
      const fallbackModelId = config.defaultModelId;
      let response: unknown;
      try {
        // 先规范化完整消息列表，格式错误时不发起无意义的模型列表请求。
        const messages = normalizeMessages(input.messages);
        // completion 前先通过模型中心校验选择，避免客户端旧默认 ID 命中已禁用模型。
        const resolvedModelId = await resolveChatModel(input.token, packageId, preferredModelId, fallbackModelId);
        const body: Record<string, unknown> = {
          model_id: resolvedModelId,
          conversation_id: input.conversationId,
          idempotency_key: normalizeIdempotencyKey(input.idempotencyKey),
          stream: false,
          messages
        };
        if (packageId > 0) body.package_id = packageId;

        const requestController = new AbortController();
        const timeoutMs = resolveLlmTimeoutMs(input.timeoutMs);
        const timeoutId = globalThis.setTimeout(() => requestController.abort("llm_timeout"), timeoutMs);

        try {
          response = await apiPost<unknown>("/v1/client/llm/chat/completions", { token: input.token, body, signal: requestController.signal });
        } catch (error) {
          if (!isModelResolutionError(error)) throw error;
          // 后台可能在页面打开后禁用模型；强制刷新列表并用新的可用模型仅重试一次。
          body.model_id = await resolveChatModel(input.token, packageId, preferredModelId, fallbackModelId, true);
          response = await apiPost<unknown>("/v1/client/llm/chat/completions", { token: input.token, body, signal: requestController.signal });
        } finally {
          globalThis.clearTimeout(timeoutId);
        }
      } catch (error) {
        // 额度不足由共享权益弹窗接管；其余模型异常才显示通用错误，避免同一失败叠两层提示。
        const entitlementHandled = await showConfiguredEntitlementDialogForAccessError(error, input.token, preferredModelId);
        if (!entitlementHandled) showLlmErrorNotice(error);
        throw error;
      }

      return normalizeCompletion(readContent(response), input.responseMode);
    } finally {
      releaseInteractionLock();
    }
  }

  static async chatStream(input: LlmChatInput, onDelta?: LlmStreamHandler): Promise<string> {
    if (isAiInteractionLocked()) throw new AiInteractionBusyError();
    const releaseInteractionLock = acquireAiInteractionLock();
    try {
      const config = getGameRuntimeConfig();
      const packageId = input.packageId ?? config.packageId;
      const preferredModelId = input.modelId;
      const fallbackModelId = config.defaultModelId;
      try {
        const messages = normalizeMessages(input.messages);
        const resolvedModelId = await resolveChatModel(input.token, packageId, preferredModelId, fallbackModelId);
        const body: Record<string, unknown> = {
          model_id: resolvedModelId,
          conversation_id: input.conversationId,
          idempotency_key: normalizeIdempotencyKey(input.idempotencyKey),
          stream: true,
          messages
        };
        if (packageId > 0) body.package_id = packageId;
        const requestController = new AbortController();
        const timeoutMs = resolveLlmTimeoutMs(input.timeoutMs);
        const timeoutId = globalThis.setTimeout(() => requestController.abort("llm_timeout"), timeoutMs);
        try {
          let response: Response;
          try {
            response = await apiPostStream("/v1/client/llm/chat/completions", { token: input.token, body, signal: requestController.signal });
          } catch (error) {
            if (!isModelResolutionError(error)) throw error;
            body.model_id = await resolveChatModel(input.token, packageId, preferredModelId, fallbackModelId, true);
            response = await apiPostStream("/v1/client/llm/chat/completions", { token: input.token, body, signal: requestController.signal });
          }
          // Partial deltas are deliberately left untouched; only the final
          // value handed to the game's parser is eligible for syntax repair.
          const raw = await consumeStream(response, onDelta);
          return normalizeCompletion(raw, input.responseMode);
        } finally {
          globalThis.clearTimeout(timeoutId);
        }
      } catch (error) {
        const entitlementHandled = await showConfiguredEntitlementDialogForAccessError(error, input.token, preferredModelId);
        if (!entitlementHandled) showLlmErrorNotice(error);
        throw error;
      }
    } finally {
      releaseInteractionLock();
    }
  }
}
